import type { Board } from '../board/types';
import type { Roster } from '../characters/types';
import type { GameDefinition } from './definition';
import type { DuelLevel } from '../battle/aiSkill';

export type FactionId = string;
export type PieceId = number;

/** Static inputs of a game: the board and the roster the pieces come from. */
export interface GameContext {
  combatMode?: 'arena' | 'card' | 'roll' | 'watch';
  /**
   * The balance lab's switch: `'off'` gives no piece a luminosity bonus, so a measured or predicted
   * fight comes down to the two characters' own numbers. A game never sets it.
   */
  luminosity?: 'off';
  /**
   * The level each AI seat's pieces fight at in Card and Roll combat, which the board AI's odds
   * read too; omitted (human) factions use this game's Expert baseline.
   */
  cardLevels?: Partial<Record<FactionId, DuelLevel>>;
  definition?: GameDefinition;
  board: Board;
  roster: Roster;
}

export interface Piece {
  id: PieceId;
  faction: FactionId;
  character: string;
  x: number;
  y: number;
  /** Damage carried between battles; 0 means healthy. */
  damage: number;
  imprisoned: boolean;
  /**
   * Turns a neutral prisoner has left to serve (`NEUTRAL_SENTENCE`); absent or 0 for every other
   * piece, whose own peak of the cycle frees it.
   */
  sentence?: number;
  /**
   * Magic in hand. Only characters with `spellPoints` in the roster have any; it is spent casting
   * and recovered a point a round while the piece stands on a power point, never above its own
   * maximum. A piece that has run dry keeps its spells and cannot afford them, which is a
   * different thing from not knowing them — the tablet shows the two states apart.
   */
  spellPoints: number;
  /** Rounds of Freeze left: the piece cannot move or cast, but still defends its square. */
  frozen: number;
  /** Rounds of Curse left: it fights slowed, and its odds in every combat mode fall (D-31). */
  cursed: number;
}

export interface LuminosityState {
  /** 0 = darkest … LUMINOSITY_STEPS − 1 = brightest. */
  phase: number;
  direction: 1 | -1;
}

export type SpellId =
  | 'teleport'
  | 'heal'
  | 'shift-time'
  | 'exchange'
  | 'revive'
  | 'imprison'
  | 'summon-elemental'
  | 'curse'
  | 'freeze'
  | 'release';
export const ALL_SPELLS: readonly SpellId[] = [
  'teleport',
  'heal',
  'shift-time',
  'exchange',
  'revive',
  'imprison',
  'summon-elemental',
  'curse',
  'freeze',
  'release',
];

/**
 * How many rounds Curse and Freeze hold their victim, counted in that victim's own turns: each
 * one that goes by, played or passed over, wears the hold down by one.
 */
export const AFFLICTION_ROUNDS = 5;

/**
 * How many of its own turns a neutral prisoner is held (D-32). The cycle frees a Light or Dark
 * prisoner when it reaches that side's peak; a neutral side has no peak to wait for, so it serves
 * a fixed sentence instead, as long as Curse and Freeze hold.
 */
export const NEUTRAL_SENTENCE = 5;

export interface FactionState {
  id: FactionId;
  /**
   * Spells cast this game. A side no longer owns a list of spells: what can be cast is a property
   * of the piece doing the casting and of what it can afford, so the count is kept only because a
   * mage's magical protection weakens a little with each one (see `effectiveHitPoints`).
   */
  spellsCast: number;
  /** Character ids of pieces lost in battle, available to Revive. */
  graveyard: string[];
  eliminated: boolean;
}

export interface PendingBattle {
  attackerId: PieceId;
  defenderId: PieceId;
  x: number;
  y: number;
  /** One main fighter per committed faction; absent in old saves means attacker and defender. */
  participantIds?: PieceId[];
  /** Other factions offered one Join/Pass response in stable board order. */
  responders?: FactionId[];
  responseIndex?: number;
}

export type CardRole = 'pressure' | 'protection' | 'deception';
export type ElementCard = 'earth' | 'water' | 'air' | 'fire';
export type SupportCardId = `character:${string}` | `element:${ElementCard}`;

/** Persistent private card resources for one faction. Card ids are unique within a faction. */
export interface CardFactionState {
  hand: SupportCardId[];
  reserve: SupportCardId[];
  /** Strategic round in which a played card may return to reserve. */
  cooldown: Partial<Record<SupportCardId, number>>;
}

/** Saved with the board game so hands and cooldown cannot be reset by reloading. */
export interface CardMatchState {
  round: number;
  factions: Record<FactionId, CardFactionState>;
}

export type GamePhase = 'play' | 'muster' | 'battle' | 'ended';

/** Plain data, serialisable as JSON; every transition is a pure function in `game.ts`. */
export interface GameState {
  /** Moves made so far. */
  turn: number;
  /** Full rounds completed (every active faction has moved once). */
  round: number;
  /** Turn of the last battle or lasting spell; too many quiet turns after it draw the game. */
  lastProgressTurn: number;
  /** Turn order. */
  factions: FactionState[];
  activeFaction: FactionId;
  pieces: Piece[];
  nextPieceId: number;
  luminosity: LuminosityState;
  phase: GamePhase;
  pendingBattle: PendingBattle | null;
  winner: FactionId | 'draw' | null;
  /** The game ended in a draw its sides agreed to (D-46), not one the rules called. */
  agreedDraw?: true;
  /** Seeded RNG state (see `createRng`). */
  rng: number;
  /** Present only for a match whose captures use Card mode. */
  cards?: CardMatchState;
  /** Muster participants whose next normal strategic action has already been spent joining. */
  skipTurns?: FactionId[];
  engagement?: 'immediate' | 'muster';
}

/**
 * A spell now names the piece casting it. Magic used to belong to the side — one mage, one list of
 * spells, each cast once — so there was nothing to name. It belongs to a piece now, which is what
 * makes selecting a piece change what the tablet offers, and what lets a side keep some magic
 * after losing its mage.
 *
 * Shift Time is the exception and has no caster: it is the player letting the round pass.
 */
export type Move =
  | { type: 'move'; pieceId: PieceId; x: number; y: number }
  | { type: 'pass' }
  /**
   * Every side still in the game agrees to a draw (D-46). Like `pass` it is never offered
   * (`legalMoves`): the table plays it once each side asked has accepted a draw offer.
   */
  | { type: 'draw' }
  | { type: 'join-battle'; pieceId: PieceId }
  | { type: 'pass-battle' }
  | { type: 'spell'; spell: 'shift-time' }
  | { type: 'spell'; spell: 'teleport'; casterId: PieceId; pieceId: PieceId; x: number; y: number }
  | { type: 'spell'; spell: 'heal'; casterId: PieceId; pieceId: PieceId }
  | { type: 'spell'; spell: 'exchange'; casterId: PieceId; pieceId: PieceId; otherId: PieceId }
  | { type: 'spell'; spell: 'revive'; casterId: PieceId; character: string; x: number; y: number }
  | { type: 'spell'; spell: 'imprison'; casterId: PieceId; pieceId: PieceId }
  /** Slows its victim in every fight for `AFFLICTION_ROUNDS` of that victim's turns (D-31). */
  | { type: 'spell'; spell: 'curse'; casterId: PieceId; pieceId: PieceId }
  /** Holds its victim still for `AFFLICTION_ROUNDS` of its turns; it still defends its square. */
  | { type: 'spell'; spell: 'freeze'; casterId: PieceId; pieceId: PieceId }
  /** Frees one of your own pieces from Imprison or Freeze. */
  | { type: 'spell'; spell: 'release'; casterId: PieceId; pieceId: PieceId }
  /** `pieceId` is the enemy attacked; the elemental appears on `x, y`, a free cell touching it. */
  | {
      type: 'spell';
      spell: 'summon-elemental';
      casterId: PieceId;
      pieceId: PieceId;
      x: number;
      y: number;
    };

export interface BattleResult {
  /** `null` when both combatants die. */
  winnerId: PieceId | null;
  /** Damage the winner carries out of the fight. */
  winnerDamage: number;
}
